import type { MeView } from '@research-assistant/shared';

/**
 * Lightweight dependency-free store. Holds the shell's cross-cutting state
 * (identity, connectivity, client queue depth) and fans out changes to
 * subscribers via a plain listener set.
 */
export interface IdentityState {
  identity: MeView | null;
  /**
   * Platform JWT (issued by cti-identity-service) — the bearer sent on every
   * `/v1` call. Persisted to localStorage so a refresh re-resolves identity
   * from `/v1/me` without re-prompting sign-in.
   */
  platformJwt: string | null;
  /** true when the cached identity was loaded from IDB after a network miss. */
  stale: boolean;
  /**
   * Flipped to `true` the first time `setIdentity` runs (after the initial
   * `/v1/me` resolves successfully OR falls back to a cached value). Used
   * by gates that must wait for identity before making admin-bypass
   * decisions — without it, a refreshing admin user would flash the
   * maintenance screen for ~500 ms before isAdmin became true.
   */
  loaded: boolean;
  /** ClientOperationQueue depth — surfaced in the sidebar. */
  queueSize: number;
  /** Whether the server is reachable. Updated by the health-check poller in boot.tsx. */
  online: boolean;
}

type Listener = (state: IdentityState) => void;

const listeners = new Set<Listener>();

const PLATFORM_JWT_KEY = 'ct-platform-jwt';

function loadPersistedJwt(): string | null {
  try {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem(PLATFORM_JWT_KEY)
      : null;
  } catch {
    return null;
  }
}

let state: IdentityState = {
  identity: null,
  platformJwt: loadPersistedJwt(),
  stale: false,
  loaded: false,
  queueSize: 0,
  online: true,
};

function notify(): void {
  // Snapshot listeners so a subscriber unsubscribing during dispatch
  // doesn't perturb iteration.
  const snapshot = Array.from(listeners);
  for (const listener of snapshot) {
    listener(state);
  }
}

export function getState(): IdentityState {
  return state;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setIdentity(identity: MeView | null, stale = false): void {
  state = { ...state, identity, stale, loaded: true };
  notify();
}

/**
 * Store (or clear) the platform JWT. Persisted to localStorage so a tab
 * refresh re-resolves identity without re-prompting sign-in. Notifies
 * subscribers so gates (AuthGate) re-render on sign-in.
 */
export function setPlatformJwt(jwt: string | null): void {
  state = { ...state, platformJwt: jwt };
  try {
    if (typeof localStorage !== 'undefined') {
      if (jwt) localStorage.setItem(PLATFORM_JWT_KEY, jwt);
      else localStorage.removeItem(PLATFORM_JWT_KEY);
    }
  } catch {
    /* storage unavailable */
  }
  notify();
}

/**
 * Test-only: flip the `loaded` flag without setting identity. Lets
 * specs assert the loading-state branch of identity-aware gates.
 */
export function _markIdentityFailedToLoadForTests(): void {
  state = { ...state, loaded: true };
  notify();
}

export function setOnline(online: boolean): void {
  if (state.online === online) {
    return;
  }
  state = { ...state, online };
  notify();
}

export function setQueueSize(queueSize: number): void {
  if (state.queueSize === queueSize) {
    return;
  }
  state = { ...state, queueSize };
  notify();
}

/** Test-only: resets module state. */
export function _resetIdentityStoreForTests(): void {
  listeners.clear();
  state = {
    identity: null,
    platformJwt: null,
    stale: false,
    loaded: false,
    queueSize: 0,
    online: true,
  };
}
